import { keycloakAuth } from '@mfox/shared/auth/keycloak'
import { useUser } from '@mfox/shared/contexts/UserContext'
import { Button, ButtonLink, type ButtonVariant } from '@mfox/shared/ui/Button'
import { Logo } from '@mfox/shared/ui/Logo'
import { SectionLabel } from '@mfox/shared/ui/Row'
import styles from './LandingPage.module.css'

const FEATURES = [
  { title: '1:1 mentor assessment', description: 'A real mentor assesses your skill level and guides your roadmap.' },
  { title: 'Personalized roadmap', description: 'A step-by-step path built from your goal and current skills.' },
  { title: 'Practice tests', description: 'Aptitude and MCQ tests that track where you stand.' },
  { title: 'Courses', description: 'Structured lessons, videos, and readings for every stage.' },
  { title: 'Code playground', description: 'Solve real problems and run your code in the browser.' },
]

const STEPS = [
  { title: 'Set your goal', description: "Tell us the role you're aiming for and the skills you already have." },
  { title: 'Get a roadmap', description: 'We build a personalized path of skills, courses, and practice.' },
  { title: 'Practice & track progress', description: 'Work through it and watch your roadmap fill in.' },
]

/**
 * Keycloak builds go straight to the hosted sign-in form; other builds to the portal's own form at /login. An account
 * Keycloak let in but the portal refused goes to /login as well, which says why: signing in again would only bounce.
 */
function SignIn({ variant }: { variant?: ButtonVariant }) {
  const { login, accessError } = useUser()
  if (keycloakAuth && !accessError) {
    return (
      <Button variant={variant} onClick={() => void login('', '')}>
        Sign in
      </Button>
    )
  }
  return (
    <ButtonLink to="/login" variant={variant}>
      Sign in
    </ButtonLink>
  )
}

export function LandingPage() {
  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <Logo to="/" />
        <SignIn variant="text" />
      </header>

      <main className={styles.main}>
        <div className={styles.hero}>
          <h1 className={styles.title}>Your path from learning to hired.</h1>
          <p className={styles.lede}>
            1:1 mentor assessments, personalized roadmaps, practice tests, courses, and hands-on coding — for
            individual learners and the institutions that support them.
          </p>
          <div className={styles.start}>
            <SignIn />
            <p className={styles.newHere}>New here? Your college sends you an email with a link to set up your account.</p>
          </div>
        </div>

        {/* After the hero on a phone; along the foot of the page on a laptop. */}
        <section className={styles.features}>
          <SectionLabel as="h2" className={styles.featuresLabel}>What you get</SectionLabel>
          <ul className={styles.featureList}>
            {FEATURES.map((feature) => (
              <li key={feature.title}>
                <h3 className={styles.itemTitle}>{feature.title}</h3>
                <p className={styles.itemText}>{feature.description}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Last on a phone; beside the headline on a laptop. */}
        <section className={styles.steps}>
          <SectionLabel as="h2">How it works</SectionLabel>
          <ol className={styles.stepList}>
            {STEPS.map((step, i) => (
              <li key={step.title}>
                <span className={styles.stepNumber}>{i + 1}</span>
                <div>
                  <h3 className={styles.itemTitle}>{step.title}</h3>
                  <p className={styles.itemText}>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </main>
    </div>
  )
}
